import emptyStateTexture from '@/assets/empty-state-texture.svg';
import { ShimmerText } from '@/components/feedback/shimmer-text';
import { TypeWriterText } from '@/components/feedback/typewriter-text';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { close, Sidebar, SidebarHeader, toggle } from '@/features/sidebar';
import { useLocalStorage } from '@/hooks/browser/use-local-storage';
import { useNetworkStatus } from '@/hooks/browser/use-network-status';
import { useAnalytics } from '@/services/analytics';
import { servicesQuery } from '@/services/api';
import { useAuth } from '@/services/auth';
import { AccountFeatureFlag, hasFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { useSupportChat } from '@/features/support';
import { copyToClipboard } from '@/utils/clipboard';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';
import { analyzeBashCommand, containsDeleteRequest, useApprovedResources } from '@/features/copilot/tools/approval';
import {
  SUPPORTED_FILE_TYPES,
  type CopilotMessage,
  type CopilotModelMetaMessage,
  type CopilotSession,
  type CopilotStatefulMessage,
  type CopilotStatefulToolCall,
  type CopilotUploadFile,
} from '@customerio/hydra-copilot';
import {
  useCopilotApproval,
  useCopilotAvailableTools,
  useCopilotFileUpload,
  useCopilotMessages,
  useCopilotMeta,
  useCopilotQueuedMessages,
  useCopilotStatus,
  useCopilotWebSearch,
} from '@customerio/hydra-copilot/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import {
  Banner,
  Box,
  Button,
  Divider,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateGraphic,
  EmptyStateTitle,
  Form,
  Icon,
  Image,
  LabelIcon,
  Link,
  Paragraph,
  Scrollable,
  Stack,
  Strong,
  Text,
  TextArea,
  Tooltip,
  Truncated,
  useSnackbars,
  InlineStack,
  KeyboardShortcut,
} from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { motion, useReducedMotion } from 'motion/react';
import type { ComponentProps, Dispatch, SetStateAction } from 'react';
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { mergeRefs } from 'react-merge-refs';
import * as chatStyles from './chat.css';
import { FileChipList, type FileData } from './components/file-chip';
import { FileUploadArea, type FileUploadAreaRef } from './components/file-upload-area';
import { Markdown } from './components/primitives/markdown';
import { MessageAction, MessageActionsContainer, MessageContainer } from './components/primitives/message-container';
import { ToolCall, ToolCallSet } from './components/primitives/tool-call';
import { References, type Reference } from './components/references';
import { BusinessProfileUsage } from './components/references/business-profile-usage';
import { useCopilotChatContext, useCopilotSessionContext } from './components/session-provider';
import type { SuggestionState } from './components/suggestions';
import { MarqueeSuggestions, Suggestions, useSuggestions } from './components/suggestions';
import { Tour } from './components/tour';
import { groupUserMessages, type ProcessedMessage } from './helpers/group-user-messages';
import { combinedStatus, toolCallLabel } from './helpers/tool-call-label';
import { useCopilotHistory } from './hooks';

import { useIsFullScreenAssistantPage } from './index';
import * as styles from './index.css';
import { useCustomerFeedback } from '@/features/customer-feedback';
import { useMatches } from 'react-router-dom';

export function CopilotActions() {
  const session = useCopilotSessionContext();
  const workspace = useCurrentWorkspace();
  const { track } = useAnalytics();

  if (workspace == null) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTriggerButton isIconOnly icon="more" variant="subtle" size="sm" />
      <DropdownMenuContent>
        <DropdownMenuItem
          icon="add"
          onClick={() => {
            session.reset();
            track('aiAssistant', 'newChatClicked', {});
          }}
        >
          New chat
        </DropdownMenuItem>
        <DropdownMenuItem
          icon="time-window"
          onClick={() => {
            toggle('copilot-history');
          }}
        >
          History
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function CopilotName() {
  const session = useCopilotSessionContext();
  const meta = useCopilotMeta(session);
  const isV2 = hasFeatureFlag(AccountFeatureFlag.AiAssistantV2);
  const defaultName = isV2 ? 'Agent' : 'Assistant';

  return (
    <Truncated>
      <TypeWriterText text={meta.name ?? defaultName} />
    </Truncated>
  );
}

export function CopilotInput() {
  const { session, input, setInput } = useCopilotChatContext();

  return <Input session={session} input={input} setInput={setInput} />;
}

export function CopilotSupportButton() {
  const supportChat = useSupportChat();

  if (!supportChat.isVisible || !supportChat.isHandoverAllowed) {
    return null;
  }

  return (
    <Tooltip content="Chat with support" isCustom={true}>
      {(context) => (
        <Button
          variant="subtle"
          onClick={async () => {
            const success = await supportChat.openChat();
            if (success) {
              close();
            }
          }}
          isLoading={supportChat.isLoading}
          size="sm"
          isIconOnly={true}
          icon="chat-question"
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
        />
      )}
    </Tooltip>
  );
}

export function CopilotMessages() {
  const session = useCopilotSessionContext();
  const auth = useAuth();
  const messages = useCopilotMessages(session);
  const status = useCopilotStatus(session);
  const isV2 = useFeatureFlag(AccountFeatureFlag.AiAssistantV2);
  const isSending = status === 'sending';
  const isReceiving = status === 'receiving';

  const supportChat = useSupportChat();
  const { track } = useAnalytics();
  const suggestionState = useSuggestions(session);
  const history = useCopilotHistory();
  const chatContentsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (suggestionState.isVisible === true && chatContentsRef.current != null) {
      chatContentsRef.current.scrollTo({
        top: chatContentsRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [suggestionState.isVisible]);

  const hasExistingSessions = (history.data?.pages?.[0]?.body?.meta?.pagination?.total ?? 0) > 0;
  const groupedMessages = useMemo(() => groupUserMessages(messages), [messages]);

  const handleSuggestionClick = async (message: string, index: number) => {
    try {
      await session.sendMessage(message);
      track('aiAssistant', 'suggestionClicked', {
        Index: index,
      });
    } catch (error) {
      sentryCaptureException(error);
    }
  };

  return (
    <Box flexGrow="1" h="0" display="flex" flexDirection="column">
      {status === 'error' ? (
        <Box h="full" display="flex" justifyContent="center" alignItems="center" pb="800">
          <EmptyState>
            <EmptyStateTitle icon="error">Failed to load</EmptyStateTitle>
            <EmptyStateDescription>
              There was an error loading {isV2 === true ? 'Agent' : 'Assistant'}. Please try again.
            </EmptyStateDescription>
            <EmptyStateActions>
              <Button
                variant="primary"
                onClick={() => {
                  session.reset();
                }}
              >
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyState>
        </Box>
      ) : (
        <>
          <ChatContents ref={chatContentsRef} h="0" flexGrow="1" py="200">
            <Box display="flex" flexDirection="column" gap="150" className={chatStyles.scrollContentWrapper}>
              {messages.length === 0 ? (
                <Box flexGrow="1" display="flex" justifyContent="center" alignItems="center" position="relative">
                  <Image
                    src={emptyStateTexture}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      margin: 'auto',
                      opacity: 0.25,
                    }}
                  />
                  <EmptyState position="relative">
                    <EmptyStateGraphic>
                      <LabelIcon name="chat" color="purple" size="lg" />
                    </EmptyStateGraphic>
                    <EmptyStateTitle>
                      <TimeOfDayGreeting />
                      {auth.user?.first_name != null ? `, ${auth.user.first_name}` : ''}
                    </EmptyStateTitle>
                    <EmptyStateDescription>
                      You can ask me anything about Customer.io
                      {hasExistingSessions ? (
                        <>
                          {' '}
                          or{' '}
                          <Link onClick={() => toggle('copilot-history')} variant="secondary">
                            continue a chat
                          </Link>
                        </>
                      ) : null}
                      .
                    </EmptyStateDescription>
                    <EmptyStateActions>
                      <MarqueeSuggestions
                        onSuggestionClick={handleSuggestionClick}
                        shouldIncludeHumanSupport={supportChat.isHandoverAllowed}
                      />
                    </EmptyStateActions>
                  </EmptyState>
                </Box>
              ) : (
                <>
                  {groupedMessages.map((message, index) => (
                    <Message
                      key={index}
                      message={message}
                      session={session}
                      isLastMessage={!isSending && index === groupedMessages.length - 1}
                      isStreaming={isReceiving && index === groupedMessages.length - 1}
                    />
                  ))}
                  {isSending || isReceiving ? (
                    <AnimateMessage>
                      <Box px="125" py="100">
                        <Text as={ShimmerText} size="s">
                          Working…
                        </Text>
                      </Box>
                    </AnimateMessage>
                  ) : null}
                </>
              )}
            </Box>
          </ChatContents>
          {messages.length > 0 ? (
            <Box className={chatStyles.scrollContentWrapper}>
              <NextMessageSuggestions
                state={suggestionState}
                onSuggestionClick={handleSuggestionClick}
                shouldIncludeHumanSupport={supportChat.isHandoverAllowed}
              />
            </Box>
          ) : null}
        </>
      )}
    </Box>
  );
}

export function SidebarChat() {
  const workspace = useCurrentWorkspace();
  const { track } = useAnalytics();
  const safeNavigate = useSafeNavigate();
  const isFullScreenAssistantPage = useIsFullScreenAssistantPage();
  const isAiAssistantV2Enabled = hasFeatureFlag(AccountFeatureFlag.AiAssistantV2);
  const sidebarRef = useRef<HTMLDivElement>(null);
  if (workspace == null) {
    return null;
  }

  return (
    <Sidebar
      ref={sidebarRef}
      id="copilot"
      onToggle={(isOpen) => {
        if (isOpen) {
          track('aiAssistant', 'opened', {});
        } else {
          track('aiAssistant', 'closed', {});
        }
      }}
      onToggleComplete={(isOpen) => {
        if (isOpen) {
          sidebarRef.current?.querySelector<HTMLTextAreaElement>('#copilot-chat-input')?.focus();
        }
      }}
    >
      <Box display="flex" flexDirection="column" h="full" w="full">
        <SidebarHeader
          title={<CopilotName />}
          leadingActions={<CopilotActions />}
          trailingActions={
            <>
              <CopilotSupportButton />

              {isAiAssistantV2Enabled && !isFullScreenAssistantPage ? (
                <Tooltip content="Open full screen" isCustom={true}>
                  {(context) => (
                    <Button
                      variant="subtle"
                      size="sm"
                      icon="expand"
                      isIconOnly={true}
                      onClick={() => {
                        if (workspace != null) {
                          track('aiAssistant', 'expandedToFullScreen', {});
                          safeNavigate(`/workspaces/${workspace.id}/agent`);
                        }
                      }}
                      ref={context.reference.setReference}
                      {...context.reference.getReferenceProps()}
                    />
                  )}
                </Tooltip>
              ) : null}
            </>
          }
          onClose={() => {
            toggle('copilot');
          }}
        />
        <Tour />
        <CopilotMessages />
        <CopilotInput />
      </Box>
    </Sidebar>
  );
}

/**
 * This component keeps the chat scrolled to the bottom if
 * the user is already at the bottom of the chat.
 */
const ChatContents = forwardRef<
  HTMLDivElement,
  { children: React.ReactNode } & Omit<ComponentProps<typeof Scrollable>, 'overflow'>
>(({ children, ...props }, passedRef) => {
  const [isAtBottom, setIsAtBottom] = useState(true);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current == null) {
      return;
    }

    // Use MutationObserver to detect content changes
    const observer = new MutationObserver(() => {
      if (isAtBottom === true && ref.current != null) {
        ref.current.scrollTop = ref.current.scrollHeight;
      }
    });

    // Observe changes to the scrollable element and its descendants
    observer.observe(ref.current, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, [isAtBottom]);

  return (
    <Scrollable
      overflow="y"
      display="flex"
      flexDirection="column"
      gap="100"
      onTouchEdge={(edge) => {
        if (edge === 'bottom') {
          setIsAtBottom(true);
        }
      }}
      onLeaveEdge={(edge) => {
        if (edge === 'bottom') {
          setIsAtBottom(false);
        }
      }}
      ref={mergeRefs([ref, passedRef])}
      {...props}
    >
      {children}
    </Scrollable>
  );
});

function Message({
  message,
  session,
  isLastMessage = false,
  isStreaming = false,
}: {
  message: ProcessedMessage;
  session: CopilotSession;
  isLastMessage?: boolean;
  isStreaming?: boolean;
}) {
  const auth = useAuth();
  const snackbars = useSnackbars();
  const { track } = useAnalytics();

  const submitFeedbackMutation = servicesQuery.recordCopilotFeedback.useMutation({
    onSuccess: (_, variables) => {
      snackbars.create({
        message: 'Thank you for your feedback!',
      });

      if (!('meta' in message) || message.meta == null || (message.meta as any).id == null) {
        return;
      }

      session.setMessageMeta((message.meta as any).id, {
        ...message.meta,
        thumbs_up: variables.body?.thumbs_up,
        thumbs_down: variables.body?.thumbs_down,
      });
    },
    onError: (error) => {
      sentryCaptureException(error);
      snackbars.create({
        message: 'Failed to submit feedback. Please try again.',
        variant: 'error',
      });
    },
  });

  const fileChips = useMemo(() => {
    if (message.role !== 'user' || message.type !== 'grouped') return [];

    return message.contentMessages.map((contentMsg: CopilotStatefulMessage, index: number) => {
      const displayName = 'display_name' in contentMsg ? contentMsg.display_name ?? 'File' : 'File';
      const content = 'content' in contentMsg ? contentMsg.content : '';

      return {
        id: `readonly-${index}`,
        name: displayName,
        mimeType:
          'mime_type' in contentMsg ? contentMsg.mime_type ?? 'application/octet-stream' : 'application/octet-stream',
        source: content,
      } as FileData;
    });
  }, [message]);

  // // TODO: show full size file in a modal
  // const handleShowFile = useCallback(
  //   (fileId: string) => {
  //     const file = fileChips.find((f: { id: string; source: string }) => f.id === fileId);
  //     if (file != null && file.source !== '') {
  //       window.open(file.source, '_blank');
  //     }
  //   },
  //   [fileChips],
  // );

  if (message.role === 'user') {
    let content: React.ReactNode = null;

    if (message.type === 'grouped') {
      // Grouped message: text + files
      content = (
        <>
          {fileChips.length > 0 ? (
            <Box mt="100">
              <FileChipList files={fileChips} readOnly={true} session={session} />
            </Box>
          ) : null}
          {!isEmptyString(message.text) ? (
            <Markdown>{message.text}</Markdown>
          ) : (
            <Text as="p" size="s" color="subtle" fontStyle="italic">
              No message
            </Text>
          )}
        </>
      );
    } else if (message.type === 'text') {
      // Single text message
      content = <Markdown>{message.text}</Markdown>;
    } else if (message.type === 'content' && 'content' in message) {
      // Single content message - render as a file chip (same as grouped with no text)
      const singleFileChip: FileData[] = [
        {
          id: 'readonly-0',
          name: message.display_name ?? 'File',
          mimeType:
            'mime_type' in message ? message.mime_type ?? 'application/octet-stream' : 'application/octet-stream',
          source: message.content,
        },
      ];
      content = (
        <>
          <Box mt="100">
            <FileChipList files={singleFileChip} readOnly={true} session={session} />
          </Box>
          <Text as="p" size="s" color="subtle" fontStyle="italic">
            No message
          </Text>
        </>
      );
    }

    return (
      <MessageContainer variant="user">
        <Box display="flex" flexDirection="column" gap="100">
          {content}
        </Box>
      </MessageContainer>
    );
  } else if (message.role === 'model' && message.type === 'text') {
    const sources = ('meta' in message && message.meta?.sources != null ? message.meta.sources : []) as Reference[];

    const { businessProfiles, otherSources } = sources.reduce<{
      businessProfiles: Reference[];
      otherSources: Reference[];
    }>(
      (acc, source) => {
        if (source?.type === 'business_profile') {
          acc.businessProfiles.push(source);
        } else {
          acc.otherSources.push(source);
        }
        return acc;
      },
      {
        businessProfiles: [],
        otherSources: [],
      },
    );

    const hasMetaId = 'meta' in message && message.meta?.id != null;
    const hasThumbsUp = 'meta' in message && message.meta?.thumbs_up === true;
    const hasThumbsDown = 'meta' in message && message.meta?.thumbs_down === true;
    const isThought = 'thought' in message && message.thought === true;
    const isV2 = hasFeatureFlag(AccountFeatureFlag.AiAssistantV2);
    const showBusinessProfile = businessProfiles.length > 0 && isLastMessage && !isV2;
    const showReferences = otherSources.length > 0;

    if (isThought) {
      return <ThoughtMessage message={message} isStreaming={isStreaming} />;
    }

    return (
      <AnimateMessage>
        <MessageContainer variant="model">
          <Markdown isAnimating={isStreaming}>{message.text}</Markdown>
          {hasMetaId || showBusinessProfile || showReferences ? (
            <Box>
              <MessageActionsContainer isVisible={isLastMessage} alignBlock="center" alignInline="space-between">
                {hasMetaId ? (
                  <InlineStack gap="050" alignBlock="center">
                    <MessageAction
                      tooltip="Copy to clipboard"
                      onClick={async () => {
                        const success = await copyToClipboard(message.text);
                        if (success) {
                          snackbars.create({
                            message: 'Copied to clipboard',
                          });
                          track('aiAssistant', 'messageCopied', {});
                        }
                      }}
                      aria-label="Copy to clipboard"
                    >
                      <Icon name="copy-to-clipboard" size="xs" />
                    </MessageAction>
                    {!hasThumbsDown ? (
                      <MessageAction
                        tooltip="This was helpful"
                        variant={hasThumbsUp ? 'active' : 'default'}
                        onClick={
                          hasThumbsUp
                            ? undefined
                            : () => {
                                submitFeedbackMutation.mutate({
                                  params: {
                                    accountId: auth.account.id,
                                    sessionId: Number(session.id),
                                    logId: message.meta.id,
                                  },
                                  body: {
                                    thumbs_up: true,
                                  },
                                });
                                track('aiAssistant', 'thumbsUp', {});
                              }
                        }
                        aria-label="Thumbs up"
                      >
                        <Icon name="thumbs-up" size="xs" />
                      </MessageAction>
                    ) : null}
                    {!hasThumbsUp ? (
                      <MessageAction
                        tooltip="This was not helpful"
                        variant={hasThumbsDown ? 'active' : 'default'}
                        onClick={
                          hasThumbsDown
                            ? undefined
                            : () => {
                                submitFeedbackMutation.mutate({
                                  params: {
                                    accountId: auth.account.id,
                                    sessionId: Number(session.id),
                                    logId: message.meta.id,
                                  },
                                  body: {
                                    thumbs_down: true,
                                  },
                                });
                                track('aiAssistant', 'thumbsDown', {});
                              }
                        }
                        aria-label="Thumbs down"
                      >
                        <Icon name="thumbs-down" size="xs" />
                      </MessageAction>
                    ) : null}
                  </InlineStack>
                ) : (
                  <div />
                )}
                {showBusinessProfile || showReferences ? (
                  <InlineStack gap="100" alignBlock="center">
                    {showBusinessProfile ? <BusinessProfileUsage references={businessProfiles} /> : null}
                    {showReferences ? <References references={otherSources} /> : null}
                  </InlineStack>
                ) : null}
              </MessageActionsContainer>
            </Box>
          ) : null}
        </MessageContainer>
      </AnimateMessage>
    );
  } else if (message.role === 'model' && message.type === 'tool_call') {
    return <ToolCalls message={message} session={session} />;
  } else if (message.role === 'model' && message.type === 'compaction') {
    return <CompactionMessage status={message.status} summary={message.summary} />;
  } else if (message.role === 'model' && message.type === 'error') {
    return (
      <AnimateMessage>
        <Banner variant="error" title="Error">
          {message.error}
        </Banner>
      </AnimateMessage>
    );
  } else {
    // typescript hack to ensure all cases are handled
    const never: never = message;
    // eslint-disable-next-line no-console
    console.error('Unknown message type', never);
    return null;
  }
}

function CompactionMessage({ status, summary }: { status: 'compacting' | 'complete'; summary?: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const hasSummary = summary != null && summary !== '';
  const isExpandable = status === 'complete' && hasSummary;

  useEffect(() => {
    if (status !== 'compacting') {
      setProgress(100);
      return;
    }

    // Animate progress with diminishing increments (never reaches 100 until complete)
    const interval = setInterval(() => {
      setProgress((prev) => {
        const remaining = 90 - prev;
        return prev + remaining * 0.015;
      });
    }, 200);

    return () => clearInterval(interval);
  }, [status]);

  return (
    <AnimateMessage>
      <Box display="flex" flexDirection="column" gap="050" py="100">
        {status === 'compacting' ? (
          <>
            <Text size="s" color="subtle" fontStyle="italic">
              Compacting our conversation so we can keep chatting…
            </Text>
            <Box display="flex" alignItems="center" gap="100">
              <Box
                flexGrow="1"
                style={{
                  height: '4px',
                  borderRadius: themeVars.radius['full'],
                  backgroundColor: themeVars.color['surface-active'],
                  overflow: 'hidden',
                }}
              >
                <Box
                  style={{
                    height: '100%',
                    width: `${Math.round(progress)}%`,
                    borderRadius: themeVars.radius['full'],
                    backgroundColor: themeVars.color['surface-bold'],
                    transition: 'width 0.3s ease',
                  }}
                />
              </Box>
              <Text size="xs" color="subtle">
                {Math.round(progress)}%
              </Text>
            </Box>
          </>
        ) : (
          <Box
            display="flex"
            alignItems="center"
            gap="050"
            cursor={isExpandable ? 'pointer' : undefined}
            onClick={isExpandable ? () => setIsOpen((prev) => !prev) : undefined}
          >
            {isExpandable ? (
              isOpen ? (
                <Icon name="chevron-down" size="sm" />
              ) : (
                <Icon name="chevron-right" size="sm" />
              )
            ) : null}
            <Icon color="success" name="check-circle" size="sm" />
            <Text size="xs" color="subtle">
              Conversation compacted
            </Text>
          </Box>
        )}
        {isOpen && hasSummary ? (
          <Box px="100" pt="050">
            <Markdown>{summary}</Markdown>
          </Box>
        ) : null}
      </Box>
    </AnimateMessage>
  );
}

function ThoughtMessage({
  message,
  isStreaming,
}: {
  message: CopilotStatefulMessage & { type: 'text'; thought?: boolean };
  isStreaming?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const durationSeconds = 'duration' in message && typeof message.duration === 'number' ? message.duration : null;

  const formattedDuration = (() => {
    if (durationSeconds == null) return null;

    if (durationSeconds < 60) {
      return `${durationSeconds}s`;
    } else {
      const minutes = Math.floor(durationSeconds / 60);
      const seconds = durationSeconds % 60;
      return `${minutes}m ${seconds}s`;
    }
  })();

  return (
    <AnimateMessage>
      <Box
        display="flex"
        flexDirection="column"
        overflow="hidden"
        as="details"
        open={isOpen}
        onToggle={() => setIsOpen(!isOpen)}
      >
        <Box as="summary" display="flex" alignItems="center" gap="050" py="075" cursor="pointer">
          {isOpen ? <Icon name="chevron-down" size="sm" /> : <Icon name="chevron-right" size="sm" />}
          <Text
            as={formattedDuration == null ? ShimmerText : 'span'}
            color={formattedDuration == null ? 'base' : 'subtle'}
          >
            {formattedDuration != null ? `Thought for ${formattedDuration}` : 'Thinking\u2026'}
          </Text>
        </Box>
        {isOpen ? (
          <ToolCallSet>
            <Markdown isAnimating={isStreaming}>{message.text}</Markdown>
          </ToolCallSet>
        ) : null}
      </Box>
    </AnimateMessage>
  );
}

function StandardToolCallSegment({ toolCalls }: { toolCalls: CopilotStatefulToolCall[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const status = combinedStatus(toolCalls);
  const toolCount = toolCalls.length;
  const { text: labelText, suffix: labelSuffix } = toolCallLabel(toolCalls);

  return (
    <AnimateMessage>
      <Box
        display="flex"
        flexDirection="column"
        overflow="hidden"
        as="details"
        open={isOpen}
        onToggle={() => setIsOpen(!isOpen)}
      >
        <Box as="summary" display="flex" alignItems="center" gap="050" py="075" cursor="pointer">
          {isOpen ? <Icon name="chevron-down" size="sm" /> : <Icon name="chevron-right" size="sm" />}
          <Text as={status === 'executing' ? ShimmerText : 'span'} color={status === 'executing' ? 'base' : 'subtle'}>
            {labelText}
          </Text>
          {labelSuffix.length > 0 ? (
            <Text color="subtle" size="xs">
              ({labelSuffix})
            </Text>
          ) : null}
        </Box>
        <ToolCallSet>
          {toolCalls.map((toolCall, index) => (
            <ToolCall
              key={index}
              status={toolCall.status}
              name={toolCall.name}
              error={toolCall?.response?.error}
              summary={toolCount > 1 ? toolCall.summary : undefined}
            />
          ))}
        </ToolCallSet>
      </Box>
    </AnimateMessage>
  );
}

function ToolCalls({
  message,
  session,
}: {
  message: CopilotStatefulMessage & { type: 'tool_call' };
  session: CopilotSession;
}) {
  const tools = useCopilotAvailableTools(session);
  const ApprovalComponent = useCopilotApproval();

  const resolveToolRender = (toolCall: (typeof message.tool_calls)[0]) => {
    // Only use the approval renderer while the approval is still pending (executing).
    // Completed approvals fall through to standard rendering.
    if (toolCall.needs_approval === true && toolCall.status === 'executing' && ApprovalComponent != null) {
      return (props: { session: CopilotSession } & (typeof message.tool_calls)[0]) => {
        const sendApprovalResponse = (decision: { status: 'approved' | 'rejected'; reason?: string }) => {
          props.session.sendMessage([
            decision.status === 'approved'
              ? {
                  role: 'user',
                  type: 'tool_response',
                  tool_responses: [
                    {
                      tool_call_id: props.id,
                      name: props.name,
                      response: { result: decision, error: null },
                      finished: true,
                    },
                  ],
                  timestamp: Math.floor(Date.now() / 1000),
                }
              : buildApprovalDenialMessage([{ id: props.id, name: props.name }], decision.reason),
          ]);
        };

        return (
          <ApprovalComponent
            toolCall={props}
            session={props.session}
            onApprove={() => sendApprovalResponse({ status: 'approved' })}
            onDeny={(reason) => sendApprovalResponse({ status: 'rejected', reason })}
          />
        );
      };
    }
    return tools[toolCall.name]?.render;
  };
  // Build ordered segments that preserve the original tool call order.
  // Consecutive non-custom tools are grouped into a single collapsible block;
  // custom-rendered tools (approvals, etc.) are rendered individually in place.
  type CustomSegment = { type: 'custom'; toolCall: (typeof message.tool_calls)[0] };
  type StandardSegment = { type: 'standard'; toolCalls: (typeof message.tool_calls)[0][] };
  type Segment = CustomSegment | StandardSegment;

  const segments = message.tool_calls.reduce<Segment[]>((acc, toolCall) => {
    // Only use custom approval rendering while the approval is still pending.
    // Once completed (approved/rejected), render as a standard collapsible tool call.
    const isActiveApproval =
      toolCall.needs_approval === true && toolCall.status === 'executing' && ApprovalComponent != null;
    const hasCustomRender = isActiveApproval || resolveToolRender(toolCall) != null;

    if (hasCustomRender) {
      return [...acc, { type: 'custom', toolCall }];
    }

    const lastSegment = acc[acc.length - 1];
    if (lastSegment != null && lastSegment.type === 'standard') {
      return [...acc.slice(0, -1), { ...lastSegment, toolCalls: [...lastSegment.toolCalls, toolCall] }];
    }

    return [...acc, { type: 'standard', toolCalls: [toolCall] }];
  }, []);

  return (
    <>
      {segments.map((segment, segmentIndex) => {
        if (segment.type === 'custom') {
          const render = resolveToolRender(segment.toolCall);

          if (render == null) {
            return (
              <AnimateMessage key={segmentIndex}>
                <ToolCallSet>
                  <ToolCall
                    status={'error'}
                    name={segment.toolCall.name}
                    error={'Something went wrong, we are missing the render function for this tool.'}
                  />
                </ToolCallSet>
              </AnimateMessage>
            );
          }

          return (
            <AnimateMessage key={segmentIndex}>
              <Box>{render({ ...segment.toolCall, session })}</Box>
            </AnimateMessage>
          );
        }

        return <StandardToolCallSegment key={segmentIndex} toolCalls={segment.toolCalls} />;
      })}
    </>
  );
}

const SUGGESTIONS_ANIMATION_DURATION = 0.24;
const SUGGESTIONS_ANIMATION_EASE: [number, number, number, number] = [0.2, 0, 0.38, 0.9];
const SUGGESTIONS_TRANSITION = {
  duration: SUGGESTIONS_ANIMATION_DURATION,
  ease: SUGGESTIONS_ANIMATION_EASE,
} as const;

function NextMessageSuggestions({
  state,
  onSuggestionClick,
  shouldIncludeHumanSupport = false,
}: {
  state: SuggestionState;
  onSuggestionClick: (message: string, index: number) => void;
  shouldIncludeHumanSupport?: boolean;
}) {
  const scrollableRef = useRef<HTMLDivElement>(null);
  const [suggestionsHidden, setSuggestionsHidden] = useLocalStorage('copilot-suggestions-hidden', false);
  const [isCollapsing, setIsCollapsing] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);
  const { track } = useAnalytics();
  const shouldReduceMotion = useReducedMotion();

  const isContentVisible = !suggestionsHidden || isCollapsing;
  const isContentInert = isCollapsing || isExpanding;

  const handleToggleSuggestions = useCallback(() => {
    if (suggestionsHidden) {
      setSuggestionsHidden(false);
      setIsExpanding(true);
      track('aiAssistant', 'suggestionsShown', {});
    } else {
      track('aiAssistant', 'suggestionsHidden', {});
      if (shouldReduceMotion === true) {
        setSuggestionsHidden(true);
      } else {
        setIsCollapsing(true);
      }
    }
  }, [suggestionsHidden, shouldReduceMotion, setSuggestionsHidden, track]);

  const handleCollapseComplete = useCallback(() => {
    setSuggestionsHidden(true);
    setIsCollapsing(false);
  }, [setSuggestionsHidden]);

  const handleExpandComplete = useCallback(() => {
    setIsExpanding(false);
  }, []);

  if (state.isVisible === false || state.isLoading === true) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: shouldReduceMotion === true ? 0 : 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={shouldReduceMotion === true ? { duration: 0 } : SUGGESTIONS_TRANSITION}
    >
      <Box flexShrink="0">
        <Divider />
        <Box display="flex" alignItems="center" justifyContent="space-between" px="150" pt="100">
          <Text size="xs">
            <Link
              icon={suggestionsHidden ? 'view-on' : 'view-off'}
              isUnderlined={false}
              variant="secondary"
              onClick={handleToggleSuggestions}
              aria-expanded={!suggestionsHidden}
            >
              {suggestionsHidden ? 'Show suggestions' : 'Hide suggestions'}
            </Link>
          </Text>
          <Box
            {...(suggestionsHidden || isContentInert ? { inert: true, style: { visibility: 'hidden' as const } } : {})}
          >
            <Button
              isIconOnly={true}
              icon="chevron-left"
              variant="subtle"
              size="sm"
              aria-label="Scroll suggestions left"
              onClick={() => {
                if (scrollableRef.current != null) {
                  scrollableRef.current.scrollTo({
                    left: scrollableRef.current.scrollLeft - scrollableRef.current.clientWidth,
                    behavior: 'smooth',
                  });
                }
              }}
            />
            <Button
              isIconOnly={true}
              icon="chevron-right"
              variant="subtle"
              size="sm"
              aria-label="Scroll suggestions right"
              onClick={() => {
                if (scrollableRef.current != null) {
                  scrollableRef.current.scrollTo({
                    left: scrollableRef.current.scrollLeft + scrollableRef.current.clientWidth,
                    behavior: 'smooth',
                  });
                }
              }}
            />
          </Box>
        </Box>
        {isContentVisible ? (
          <motion.div
            initial={isExpanding ? { gridTemplateRows: '0fr' } : false}
            animate={{ gridTemplateRows: isCollapsing ? '0fr' : '1fr' }}
            transition={shouldReduceMotion === true ? { duration: 0 } : SUGGESTIONS_TRANSITION}
            className={chatStyles.suggestionsCollapseGrid}
            onAnimationComplete={() => {
              if (isCollapsing) {
                handleCollapseComplete();
              }
              if (isExpanding) {
                handleExpandComplete();
              }
            }}
            {...(isContentInert ? { inert: true } : {})}
          >
            <div className={chatStyles.suggestionsCollapseContent}>
              <Scrollable
                overflow="x"
                display="flex"
                gap="100"
                ref={scrollableRef}
                p="100"
                className={chatStyles.hiddenScrollbar}
              >
                <Suggestions
                  suggestions={state.suggestions}
                  onSuggestionClick={onSuggestionClick}
                  shouldShowPlaceholder={false}
                  shouldIncludeHumanSupport={shouldIncludeHumanSupport}
                />
              </Scrollable>
            </div>
          </motion.div>
        ) : null}
      </Box>
    </motion.div>
  );
}

/**
 * Builds a tool_response message that rejects one or more pending approval tool calls.
 */
function buildApprovalDenialMessage(approvals: Array<{ id: string; name: string }>, reason?: string): CopilotMessage {
  return {
    role: 'user',
    type: 'tool_response',
    tool_responses: approvals.map((approval) => ({
      tool_call_id: approval.id,
      name: approval.name,
      response: { result: { status: 'rejected' as const, reason }, error: null },
      finished: true,
    })),
    timestamp: Math.floor(Date.now() / 1000),
  };
}

/**
 * Returns pending approval tool calls from the session's stateful messages.
 * A pending approval is a tool call with `needs_approval === true` and `status === 'executing'`.
 */
function usePendingApprovals(session: CopilotSession) {
  const messages = useCopilotMessages(session);

  return useMemo(() => {
    const pending: Array<{ id: string; name: string; args?: Record<string, unknown> }> = [];
    for (const message of messages) {
      if (message.role === 'model' && message.type === 'tool_call') {
        for (const toolCall of message.tool_calls) {
          if (toolCall.needs_approval === true && toolCall.status === 'executing') {
            pending.push({ id: toolCall.id, name: toolCall.name, args: toolCall.args });
          }
        }
      }
    }
    return pending;
  }, [messages]);
}

function Input({
  session,
  input,
  setInput,
}: {
  input: string;
  setInput: Dispatch<SetStateAction<string>>;
  session: CopilotSession;
}) {
  const lastInput = useRef<string | null>(null);
  const lastFiles = useRef<CopilotUploadFile[]>([]);
  const fileUploadAreaRef = useRef<FileUploadAreaRef>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const snackbars = useSnackbars();
  const { track } = useAnalytics();
  const status = useCopilotStatus(session);
  const isLoading = status === 'sending' || status === 'receiving';
  const queuedMessages = useCopilotQueuedMessages(session);
  const networkStatus = useNetworkStatus();
  const pendingApprovals = usePendingApprovals(session);

  // File upload functionality
  const fileUpload = useCopilotFileUpload(session);

  // Handle files from both drag drop and file input
  const handleFiles = useCallback(
    (files: File[]) => {
      const result = fileUpload.addFiles(files);

      const messages: string[] = [];

      if (result.errors.length > 0) {
        messages.push(...result.errors.map((e) => `${e.name}: ${e.error}`));
      }

      if (messages.length > 0) {
        setUploadError(messages.join('\n'));
      }

      // Immediately start uploading the files
      if (result.addedCount > 0) {
        fileUpload
          .uploadAllFiles()
          .then((uploadResult) => {
            // Handle failed uploads - accumulate with validation errors
            if (uploadResult.failed.length > 0) {
              const failedMessages = uploadResult.failed.map((f) => `${f.name}: ${f.error}`);

              // Append to existing validation errors if any
              setUploadError((prevError) => {
                if (!isEmptyString(prevError)) {
                  return prevError + '\n' + failedMessages.join('\n');
                }
                return failedMessages.join('\n');
              });

              // Report to Sentry
              for (const f of uploadResult.failed) {
                sentryCaptureException(new Error(`File upload failed for ${f.name}: ${f.error}`));
              }
            } else if (uploadResult.successful.length > 0 && messages.length === 0) {
              // Clear error if all uploads succeeded and there were no validation errors
              setUploadError(null);
            }

            if (uploadResult.successful.length > 0) {
              track('aiAssistant', 'fileUploaded', { Count: uploadResult.successful.length });
            }
          })
          .catch((error) => {
            // This catch should rarely be hit since uploadAllFiles uses Promise.allSettled
            sentryCaptureException(error);
            setUploadError(error instanceof Error ? error.message : 'Failed to upload files');
          });
      }
    },
    [fileUpload, track],
  );

  const fileChips = useMemo(
    () =>
      fileUpload.files.map((file) => ({
        id: file.id,
        name: file.name,
        size: file.size,
        mimeType: file.mimeType,
        source: file.file,
        status: file.status,
      })),
    [fileUpload.files],
  );

  async function submit(input: string, files: CopilotUploadFile[] = []) {
    setError(null);
    try {
      // Create message payload
      const messages: CopilotMessage[] = [];

      // Add file messages for uploaded files
      const uploadedFiles = files.filter((f) => f.status === 'uploaded' && f.uploadedUrl != null);
      for (const file of uploadedFiles) {
        messages.push({
          role: 'user',
          type: 'content',
          content: file.uploadedUrl ?? '',
          mime_type: file.mimeType,
          display_name: file.name,
        });
      }

      // Add text message if there's input
      if (!isEmptyString(input)) {
        messages.push({
          role: 'user',
          type: 'text',
          text: input,
        });
      }

      // Clear input and files indicating that the user has sent the message
      setInput('');
      fileUpload.clearFiles();

      // If we have messages to send, send them
      if (messages.length > 0) {
        await session.sendMessage(messages);
        track('aiAssistant', 'messageSent', {
          'Has Files': uploadedFiles.length > 0,
          'File Count': uploadedFiles.length,
        });
      }
    } catch (error) {
      sentryCaptureException(error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    }
  }

  const handleUploadButtonClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (fileUploadAreaRef.current != null) {
      fileUploadAreaRef.current.triggerFileInput();
    }
  }, []);

  const handleStop = useCallback(async () => {
    track('aiAssistant', 'stopClicked', {});
    const { messages, error } = await session.stop();

    // Always restore queued messages to the input box, even if the backend
    // cancellation failed — the local stream is already aborted either way.
    const texts = messages
      .filter((m): m is CopilotMessage & { text: string } => m.type === 'text' && 'text' in m)
      .map((m) => m.text);
    if (texts.length > 0) {
      // Use a functional updater so we always prepend onto the latest input
      // value — not the one captured when this callback was created. The
      // component becomes interactive as soon as the stream aborts (before
      // stopExchange resolves), so the user may have typed in the window
      // between abort and the await returning.
      const queued = texts.join('\n\n');
      setInput((prev) => [queued, prev].filter((t) => t.trim() !== '').join('\n\n'));
    }

    if (error != null) {
      sentryCaptureException(error);
      snackbars.create({
        message: 'Failed to stop the response. Please try again.',
        variant: 'error',
      });
    }
  }, [session, setInput, snackbars, track]);

  const isV2 = hasFeatureFlag(AccountFeatureFlag.AiAssistantV2);
  const { isAllApproved, setIsAllApproved } = useApprovedResources();
  const enableWebSearch = useCopilotWebSearch(session);
  const workspace = useCurrentWorkspace();
  const safeNavigate = useSafeNavigate();
  const showFeedbackModal = useCustomerFeedback();
  const matches = useMatches();
  const lastMatch = matches[matches.length - 1];

  // Auto-approve pending approval requests when "Can make changes" is enabled.
  // DELETE curl requests always require manual approval, even with auto-approve on.
  // Track which approvals we've already approved to avoid sending duplicates
  // if the effect re-runs before the messages are processed.
  const approvedIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!isAllApproved || pendingApprovals.length === 0) {
      return;
    }

    const newApprovals = pendingApprovals.filter((a) => {
      if (approvedIdsRef.current.has(a.id)) return false;

      // Never auto-approve bash commands that contain DELETE curl requests.
      // If command isn't a string, we can't check for DELETE — require manual approval.
      // Otherwise, auto-approve even if the command couldn't be fully analyzed.
      if (a.name === 'bash') {
        if (typeof a.args?.command !== 'string') return false;
        const analysis = analyzeBashCommand(a.args.command);
        if (containsDeleteRequest(analysis)) return false;
      }

      return true;
    });
    if (newApprovals.length === 0) {
      return;
    }

    for (const approval of newApprovals) {
      approvedIdsRef.current.add(approval.id);
    }

    session
      .sendMessage(
        newApprovals.map((approval) => ({
          role: 'user' as const,
          type: 'tool_response' as const,
          tool_responses: [
            {
              tool_call_id: approval.id,
              name: approval.name,
              response: { result: { status: 'approved' as const }, error: null },
              finished: true,
            },
          ],
          timestamp: Math.floor(Date.now() / 1000),
        })),
      )
      .catch(() => {
        // Remove IDs so the effect can retry on the next render
        for (const approval of newApprovals) {
          approvedIdsRef.current.delete(approval.id);
        }
      });
  }, [isAllApproved, pendingApprovals, session]);

  const hasContent = useMemo<boolean>(
    () => !isEmptyString(input) || fileUpload.files.length > 0,
    [input, fileUpload.files],
  );
  const hasUploadingFiles = useMemo<boolean>(
    () => fileUpload.files.some((f) => f.status === 'uploading' || f.status === 'pending'),
    [fileUpload.files],
  );
  const canSubmit = hasContent && !hasUploadingFiles;

  return (
    <Box p="100">
      {uploadError != null ? (
        <Banner
          title="Upload Error"
          variant="error"
          mb="100"
          shouldShowDismissButton={true}
          onClose={() => setUploadError(null)}
        >
          <Box style={{ whiteSpace: 'pre-line' }}>{uploadError}</Box>
        </Banner>
      ) : null}
      {error != null ? (
        <Banner
          title="Error"
          variant="error"
          mb="100"
          action={{
            label: 'Retry',
            onClick: () => {
              if (lastInput.current != null || lastFiles.current.length > 0) {
                submit(lastInput.current ?? '', lastFiles.current);
              }
            },
          }}
          shouldShowDismissButton={true}
          onClose={() => setError(null)}
        >
          {error}
        </Banner>
      ) : null}

      {isLoading && queuedMessages.length > 0 ? (
        <Box
          mb="100"
          p="100"
          display="flex"
          flexDirection="column"
          gap="075"
          className={chatStyles.queuedMessagesContainer}
        >
          <InlineStack
            alignBlock="center"
            alignInline="space-between"
            pb="050"
            className={chatStyles.queuedMessagesHeader}
          >
            <InlineStack alignBlock="center" gap="050">
              <Icon name="time-window" size="xs" color="subtle" />
              <Text size="xs" color="subtle" weight="medium">
                Queued messages
              </Text>
            </InlineStack>
            <InlineStack alignBlock="center" gap="050">
              <Text size="xs" color="subtle">
                Press
              </Text>
              <KeyboardShortcut hotkey="ArrowUp" />
              <Text size="xs" color="subtle">
                to edit
              </Text>
            </InlineStack>
          </InlineStack>
          <QueuedMessage messages={queuedMessages} />
        </Box>
      ) : null}
      <FileUploadArea
        ref={fileUploadAreaRef}
        onFilesSelected={handleFiles}
        disabled={networkStatus === 'offline'}
        style={{ position: 'relative' }}
        acceptedMimeTypes={Object.keys(SUPPORTED_FILE_TYPES)}
      >
        <Form
          onSubmit={async (e) => {
            e.preventDefault();

            if (!canSubmit) {
              return;
            }

            if (networkStatus === 'offline') {
              return;
            }

            // Only update retry refs if this is a direct send (not queued).
            // If the session is busy, sendMessage will queue — don't overwrite
            // the retry refs for the in-flight message.
            if (status !== 'sending' && status !== 'receiving') {
              lastInput.current = input;
              lastFiles.current = Array.from(fileUpload.files);
            }
            await submit(input, Array.from(fileUpload.files));
          }}
          position="relative"
          autoLoading={false}
        >
          <TextArea
            value={input}
            onInput={(e) => setInput(e.target.value)}
            onKeyDown={(e: KeyboardEvent) => {
              // Arrow up at the top of the input: pull all queued messages back into the editor
              if (e.key === 'ArrowUp' && queuedMessages.length > 0) {
                const textarea = e.target as HTMLTextAreaElement;
                const isAtTop = textarea.selectionStart === 0 && textarea.selectionEnd === 0;
                if (isAtTop) {
                  e.preventDefault();
                  const cleared = session.clearQueuedMessages();
                  const texts = cleared
                    .filter((m): m is CopilotMessage & { text: string } => 'text' in m && m.type === 'text')
                    .map((m) => m.text);
                  const restoredText = texts.join('\n');
                  const combined = [restoredText, input].filter((t) => t.trim() !== '').join('\n\n');
                  setInput(combined);
                }
              }
            }}
            onPaste={(e) => {
              // Extract image files from clipboard
              const items = Array.from(e.clipboardData?.items ?? []);
              const imageFiles = items
                .filter((item) => item.type.startsWith('image/'))
                .map((item) => item.getAsFile())
                .filter((file): file is File => file !== null);

              if (imageFiles.length > 0) {
                e.preventDefault();
                handleFiles(imageFiles);
              }
            }}
            id="copilot-chat-input"
            placeholder={fileChips.length > 0 ? 'Add a message (optional)' : 'How can I help you today?'}
            shouldSubmitOnEnter={true}
            ariaLabel={isV2 === true ? 'Send message to Agent' : 'Send message to Assistant'}
            resize="none"
            minRows={1}
            maxRows={6}
            unsafe_textareaWrapperClassName={
              fileChips.length > 0
                ? `${styles.chatTextareaWrapper} ${styles.chatTextareaWrapperWithFiles}`
                : styles.chatTextareaWrapper
            }
            unsafe_inputClassName={styles.chatTextareaInput}
          />

          {/* File chips positioned absolutely */}
          {fileChips.length > 0 ? (
            <Box
              style={{
                position: 'absolute',
                top: themeVars.space['100'],
                left: themeVars.space['100'],
                right: themeVars.space['600'],
                zIndex: 1,
                pointerEvents: 'none',
              }}
            >
              <Box style={{ pointerEvents: 'auto' }}>
                <FileChipList files={fileChips} readOnly={false} onRemove={fileUpload.removeFile} session={session} />
              </Box>
            </Box>
          ) : null}

          <Box
            display="flex"
            alignItems="flex-end"
            justifyContent="space-between"
            style={{
              position: 'absolute',
              bottom: themeVars.space['100'],
              left: themeVars.space['100'],
              right: themeVars.space['100'],
              pointerEvents: 'none',
            }}
          >
            <Box display="flex" alignItems="center" gap="050" style={{ pointerEvents: 'auto' }}>
              {isV2 ? (
                <>
                  <Button
                    icon="paperclip"
                    isIconOnly={true}
                    variant="tertiary"
                    type="button"
                    size="sm"
                    style={{ border: 'none' }}
                    isDisabled={networkStatus === 'offline'}
                    onClick={handleUploadButtonClick}
                  />
                  <DropdownMenu>
                    <DropdownMenuTriggerButton
                      isIconOnly
                      icon="properties"
                      variant="tertiary"
                      size="sm"
                      style={{ border: 'none' }}
                    />
                    <DropdownMenuContent>
                      <DropdownMenuGroup label="Capabilities">
                        <DropdownMenuCheckboxItem
                          isChecked={isAllApproved}
                          onCheckedChange={(checked) => {
                            setIsAllApproved(checked);
                            track('aiAssistant', 'applyChangesToggled', { Enabled: checked });
                          }}
                          shouldCloseOnClick={false}
                        >
                          Apply changes automatically
                        </DropdownMenuCheckboxItem>
                        <DropdownMenuCheckboxItem
                          isChecked={enableWebSearch}
                          onCheckedChange={(checked) => {
                            session.setWebSearchEnabled(checked);
                            track('aiAssistant', 'webSearchToggled', { Enabled: checked });
                          }}
                          shouldCloseOnClick={false}
                        >
                          Enable web search
                        </DropdownMenuCheckboxItem>
                      </DropdownMenuGroup>
                      <DropdownMenuGroup label="Settings">
                        {workspace != null ? (
                          <DropdownMenuItem
                            isDisabled={lastMatch?.id === 'workspace_settings_ai_business_profile'}
                            onClick={() => {
                              track('aiAssistant', 'editBusinessProfileClicked', { 'Click Location': 'menu' });
                              safeNavigate(`/workspaces/${workspace.id}/settings/ai-business-profile`);
                            }}
                          >
                            Add context
                          </DropdownMenuItem>
                        ) : null}
                        <DropdownMenuItem
                          onClick={() => {
                            track('aiAssistant', 'feedbackLinkClicked', {});
                            void showFeedbackModal({
                              feature: isV2 === true ? 'AI Agent' : 'AI Assistant',
                              onSubmit: (values) => {
                                track('aiAssistant', 'feedbackFormSubmitted', {
                                  Feedback: values.feedback,
                                });
                              },
                            });
                          }}
                        >
                          Give feedback
                        </DropdownMenuItem>
                      </DropdownMenuGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : null}
              <TokenUsage session={session} />
            </Box>
            <Box display="flex" alignItems="center" gap="050" style={{ pointerEvents: 'auto' }}>
              {!isV2 ? (
                <Button
                  icon="paperclip"
                  isIconOnly={true}
                  variant="tertiary"
                  type="button"
                  size="sm"
                  style={{ border: 'none' }}
                  isDisabled={networkStatus === 'offline'}
                  onClick={handleUploadButtonClick}
                />
              ) : null}
              {isLoading ? (
                <Button icon="stop" isIconOnly={true} variant="primary" type="button" size="sm" onClick={handleStop} />
              ) : (
                <Button
                  icon="chevron-up"
                  isIconOnly={true}
                  variant="primary"
                  type="submit"
                  size="sm"
                  isDisabled={networkStatus === 'offline' || !canSubmit}
                />
              )}
            </Box>
          </Box>
        </Form>
      </FileUploadArea>
      {!isV2 ? <FooterStatment input={input} /> : null}
    </Box>
  );
}

/**
 * Extracts the latest token usage info from the session's raw messages.
 * Scans backwards through messages to find the most recent meta message
 * that includes token usage data.
 */
function useTokenUsage(session: CopilotSession) {
  const messages = useStore(session.__messages);

  return useMemo(() => {
    // Scan backwards for the most recent meta message with token usage
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role === 'model' && msg.type === 'meta' && 'meta' in msg) {
        const meta = (msg as CopilotModelMetaMessage).meta;
        if (meta?.prompt_tokens_used != null && meta.prompt_tokens_used > 0) {
          return {
            promptTokensUsed: meta.prompt_tokens_used,
            contextWindowSize: meta.context_window_size ?? 0,
          };
        }
      }
    }
    return null;
  }, [messages]);
}

function TokenUsage({ session }: { session: CopilotSession }) {
  const usage = useTokenUsage(session);

  if (usage == null) {
    return <Box />;
  }

  const { promptTokensUsed, contextWindowSize } = usage;
  const percentage = contextWindowSize > 0 ? Math.round((promptTokensUsed / contextWindowSize) * 100) : null;

  if (percentage == null || percentage < 80) {
    return <Box />;
  }

  const tooltipContent = `This conversation is using ${percentage}% of the available memory. As it grows, older details may be dropped to fit new messages.`;

  return (
    <Tooltip content={tooltipContent} isCustom={true}>
      {(context) => (
        <Paragraph
          text="product-pxs-secondary"
          color="subtle"
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
        >
          {percentage}% memory used
        </Paragraph>
      )}
    </Tooltip>
  );
}

function FooterStatment({ input }: { input: string }) {
  const isV2 = useFeatureFlag(AccountFeatureFlag.AiAssistantV2);
  const networkStatus = useNetworkStatus();
  const showFeedbackModal = useCustomerFeedback();
  const { track } = useAnalytics();
  const safeNavigate = useSafeNavigate();
  const workspace = useCurrentWorkspace();
  const matches = useMatches();
  const lastMatch = matches[matches.length - 1];

  return (
    <Paragraph text="product-pxs-secondary" pt="050" color="subtle">
      {networkStatus === 'offline' ? (
        <>{input.length > 0 ? <>You can't send messages when offline</> : <>You are currently offline</>}</>
      ) : input.length > 0 ? (
        <>
          <Strong weight="medium">Shift + Return</Strong> to add a new line
        </>
      ) : (
        <InlineStack alignInline="space-between">
          {workspace != null ? (
            <Link
              variant="secondary"
              isDisabled={lastMatch?.id === 'workspace_settings_ai_business_profile'}
              onClick={() => {
                track('aiAssistant', 'editBusinessProfileClicked', { 'Click Location': 'footer' });
                safeNavigate(`/workspaces/${workspace.id}/settings/ai-business-profile`);
              }}
            >
              Add context
            </Link>
          ) : null}
          <Link
            variant="secondary"
            onClick={() => {
              track('aiAssistant', 'feedbackLinkClicked', {});
              void showFeedbackModal({
                feature: isV2 === true ? 'AI Agent' : 'AI Assistant',
                onSubmit: (values) => {
                  track('aiAssistant', 'feedbackFormSubmitted', {
                    Feedback: values.feedback,
                  });
                },
              });
            }}
          >
            Give feedback
          </Link>
        </InlineStack>
      )}
    </Paragraph>
  );
}

function QueuedMessage({ messages }: { messages: CopilotMessage[] }) {
  const textMessages = useMemo(
    () => messages.filter((m): m is CopilotMessage & { text: string } => m.type === 'text' && 'text' in m),
    [messages],
  );

  if (textMessages.length === 0) {
    return null;
  }

  return (
    <Stack gap="050">
      {textMessages.map((m, index) => (
        <Text key={index} size="xs" color="subtle">
          <Truncated>{m.text}</Truncated>
        </Text>
      ))}
    </Stack>
  );
}

function AnimateMessage({ children }: { children: React.ReactNode }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}>
      {children}
    </motion.div>
  );
}

function TimeOfDayGreeting() {
  const date = new Date();
  const hours = date.getHours();

  if (hours < 12) {
    return 'Good morning';
  } else if (hours < 18) {
    return 'Good afternoon';
  } else {
    return 'Good evening';
  }
}
